/**
 * WindowIdManager
 * 
 * Manages unique window/tab IDs with duplicate tab detection.
 * Uses localStorage registry and heartbeat mechanism to track active windows
 * and automatically clean up stale entries from closed tabs.
 * 
 * The heartbeat mechanism provides empirical duplicate detection and automatic
 * recovery of orphaned IDs that may occur due to race conditions.
 */
class WindowIdManager {
  private _windowIdRegistryKey: string = "jitux_window_id_registry";
  private _heartbeatIntervalId: number | null = null;
  private _cleanupIntervalId: number | null = null;
  private _storageAvailable: boolean = true;
  private _isRegisteringWindowId: boolean = false;
  private _currentWindowId: string | null = null;

  constructor() {
    this.initialize();
  }

  /**
   * Gets the current window ID. Creates one if it doesn't exist.
   */
  public getWindowId(): string {
    if (this._currentWindowId) {
      return this._currentWindowId;
    }

    let id: string | null = null;
    try {
      id = window.sessionStorage.getItem("windowId");
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error getting windowId from sessionStorage:", e);
    }

    if (!id) {
      id = this.generateWindowId();
      this.setWindowId(id);
      this.registerWindowId(id);
    }

    this._currentWindowId = id;
    return id;
  }

  /**
   * Manually cleanup resources. Called automatically on beforeunload.
   */
  public cleanup(): void {
    try {
      // Unregister window ID
      if (this._currentWindowId) {
        this.unregisterWindowId(this._currentWindowId);
      }

      // Clear intervals
      if (this._heartbeatIntervalId !== null) {
        clearInterval(this._heartbeatIntervalId);
        this._heartbeatIntervalId = null;
      }
      if (this._cleanupIntervalId !== null) {
        clearInterval(this._cleanupIntervalId);
        this._cleanupIntervalId = null;
      }
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error during cleanup:", e);
    }
  }

  /**
   * Initialize the window ID manager and setup duplicate tab detection.
   */
  private initialize(): void {
    // Check storage availability first
    this._storageAvailable = this.isStorageAvailable();

    if (!this._storageAvailable) {
      console.warn("[JIT-UX WindowIdManager] Storage unavailable, duplicate tab detection disabled");
      return;
    }

    try {
      // Check if current windowId is already registered (meaning it's a duplicate)
      let currentId: string | null = null;
      try {
        currentId = window.sessionStorage.getItem("windowId");
      } catch (e) {
        console.error("[JIT-UX WindowIdManager] Error reading windowId from sessionStorage:", e);
      }

      if (currentId && this.isWindowIdDuplicate(currentId)) {
        // This is a duplicated tab - generate a new ID
        const newId = this.generateWindowId();
        this.setWindowId(newId);
        this.registerWindowId(newId);
        this._currentWindowId = newId;
      } else if (currentId) {
        // Register this window ID
        this.registerWindowId(currentId);
        this._currentWindowId = currentId;
      }

      // Clean up registry when window is closed
      window.addEventListener("beforeunload", () => {
        this.cleanup();
      });

      // Periodically clean up stale entries (tabs that were closed without cleanup)
      this.cleanupStaleWindowIds();
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error during initialization:", e);
    }
  }

  /**
   * Check if localStorage and sessionStorage are available and accessible.
   */
  private isStorageAvailable(): boolean {
    try {
      const testKey = "__jitux_storage_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      sessionStorage.setItem(testKey, "test");
      sessionStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn("[JIT-UX WindowIdManager] Storage not available:", e);
      return false;
    }
  }

  /**
   * Safely read from localStorage with error handling.
   */
  private safeLocalStorageGetItem(key: string): string | null {
    if (!this._storageAvailable) return null;
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error reading from localStorage:", e);
      return null;
    }
  }

  /**
   * Safely write to localStorage with error handling.
   */
  private safeLocalStorageSetItem(key: string, value: string): boolean {
    if (!this._storageAvailable) return false;
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      // Handle QuotaExceededError
      if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
        console.warn("[JIT-UX WindowIdManager] localStorage quota exceeded, attempting cleanup:", e);
        this.performEmergencyStorageCleanup();
        try {
          localStorage.setItem(key, value);
          return true;
        } catch (retryError) {
          console.error("[JIT-UX WindowIdManager] Failed to write to localStorage after cleanup:", retryError);
          return false;
        }
      }
      console.error("[JIT-UX WindowIdManager] Error writing to localStorage:", e);
      return false;
    }
  }

  /**
   * Safely remove from localStorage with error handling.
   */
  private safeLocalStorageRemoveItem(key: string): void {
    if (!this._storageAvailable) return;
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error removing from localStorage:", e);
    }
  }

  /**
   * Emergency cleanup when localStorage is full.
   */
  private performEmergencyStorageCleanup(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("jitux_heartbeat_")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore errors during cleanup
        }
      });
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error during emergency cleanup:", e);
    }
  }

  /**
   * Set the window ID in sessionStorage.
   */
  private setWindowId(value: string): void {
    try {
      window.sessionStorage.setItem("windowId", value);
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error setting windowId in sessionStorage:", e);
    }
  }

  /**
   * Generate a unique window ID.
   */
  private generateWindowId(): string {
    return (
      "<JIT-UX>" +
      new Date().valueOf().toString(16) +
      "-" +
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1)
    );
  }

  /**
   * Register a window ID in the global registry.
   */
  private registerWindowId(id: string): void {
    // Prevent concurrent registration attempts (race condition prevention)
    if (this._isRegisteringWindowId) {
      return;
    }

    if (!this._storageAvailable || !id) {
      return;
    }

    try {
      this._isRegisteringWindowId = true;

      const registry = this.getWindowIdRegistry();
      if (!registry.includes(id)) {
        registry.push(id);
        this.safeLocalStorageSetItem(this._windowIdRegistryKey, JSON.stringify(registry));
      }
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error registering window ID:", e);
    } finally {
      this._isRegisteringWindowId = false;
    }
  }

  /**
   * Get the list of all registered window IDs.
   */
  private getWindowIdRegistry(): string[] {
    if (!this._storageAvailable) {
      return [];
    }

    try {
      const registryJson = this.safeLocalStorageGetItem(this._windowIdRegistryKey);
      if (!registryJson) {
        return [];
      }

      const parsed = JSON.parse(registryJson);
      // Validate that it's an array
      if (Array.isArray(parsed)) {
        return parsed.filter(item => typeof item === 'string');
      }
      return [];
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error parsing window ID registry, resetting:", e);
      // Reset corrupted registry
      this.safeLocalStorageSetItem(this._windowIdRegistryKey, JSON.stringify([]));
      return [];
    }
  }

  /**
   * Check if a window ID already exists in the registry or has an active heartbeat.
   * This ensures proper duplicate detection even when IDs are orphaned from the registry.
   */
  private isWindowIdDuplicate(id: string): boolean {
    if (!this._storageAvailable || !id) {
      return false;
    }

    try {
      // Check if ID is in the registry
      const registry = this.getWindowIdRegistry();
      if (registry.includes(id)) {
        return true;
      }
      
      // CRITICAL: Also check for active heartbeat
      // This catches orphaned IDs that fell out of registry due to race conditions
      const heartbeatKey = `jitux_heartbeat_${id}`;
      const lastHeartbeat = this.safeLocalStorageGetItem(heartbeatKey);
      
      if (lastHeartbeat) {
        const heartbeatTime = parseInt(lastHeartbeat, 10);
        if (!isNaN(heartbeatTime)) {
          const timeSinceHeartbeat = Date.now() - heartbeatTime;
          // If heartbeat is recent (within 15 seconds), another tab is actively using this ID
          if (timeSinceHeartbeat < 15000) {
            return true;
          }
        }
      }
      
      return false;
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error checking for duplicate window ID:", e);
      return false;
    }
  }

  /**
   * Unregister a window ID from the global registry.
   */
  private unregisterWindowId(id: string): void {
    if (!this._storageAvailable || !id) {
      return;
    }

    try {
      const registry = this.getWindowIdRegistry();
      const filteredRegistry = registry.filter(registeredId => registeredId !== id);
      this.safeLocalStorageSetItem(this._windowIdRegistryKey, JSON.stringify(filteredRegistry));

      // Clean up this window's heartbeat
      this.safeLocalStorageRemoveItem(`jitux_heartbeat_${id}`);
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error unregistering window ID:", e);
    }
  }

  /**
   * Cleanup stale window IDs using a heartbeat mechanism.
   */
  private cleanupStaleWindowIds(): void {
    if (!this._storageAvailable) {
      return;
    }

    try {
      const heartbeatKey = `jitux_heartbeat_${this.getWindowId()}`;

      // Update this window's heartbeat
      const updateHeartbeat = () => {
        if (!this._storageAvailable) return;
        try {
          this.safeLocalStorageSetItem(heartbeatKey, Date.now().toString());
        } catch (e) {
          console.error("[JIT-UX WindowIdManager] Error updating heartbeat:", e);
        }
      };

      updateHeartbeat();
      this._heartbeatIntervalId = window.setInterval(updateHeartbeat, 5000); // Update every 5 seconds

      // Clean up stale window IDs every 30 seconds
      this._cleanupIntervalId = window.setInterval(() => {
        if (!this._storageAvailable) return;

        try {
          const registry = this.getWindowIdRegistry();
          const now = Date.now();
          const activeWindowIds: string[] = [];

          // Process IDs currently in registry
          registry.forEach(id => {
            if (!id || typeof id !== 'string') return;

            const hbKey = `jitux_heartbeat_${id}`;
            const lastHeartbeat = this.safeLocalStorageGetItem(hbKey);

            if (lastHeartbeat) {
              const heartbeatTime = parseInt(lastHeartbeat, 10);
              if (!isNaN(heartbeatTime)) {
                const timeSinceHeartbeat = now - heartbeatTime;
                // Keep if heartbeat is recent (within 15 seconds)
                if (timeSinceHeartbeat < 15000) {
                  activeWindowIds.push(id);
                } else {
                  // Clean up stale heartbeat
                  this.safeLocalStorageRemoveItem(hbKey);
                }
              }
            }
          });

          // CRITICAL: Find and rescue orphaned heartbeats
          // (IDs with active heartbeats but not in registry due to race conditions)
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith("jitux_heartbeat_")) {
                const orphanedId = key.replace("jitux_heartbeat_", "");
                
                // Skip if already processed
                if (activeWindowIds.includes(orphanedId)) {
                  continue;
                }
                
                // Check if this orphaned heartbeat is still active
                const lastHeartbeat = this.safeLocalStorageGetItem(key);
                if (lastHeartbeat) {
                  const heartbeatTime = parseInt(lastHeartbeat, 10);
                  if (!isNaN(heartbeatTime)) {
                    const timeSinceHeartbeat = now - heartbeatTime;
                    if (timeSinceHeartbeat < 15000) {
                      // Active orphaned heartbeat - rescue it by adding to active list!
                      activeWindowIds.push(orphanedId);
                    } else {
                      // Stale orphaned heartbeat - clean it up
                      this.safeLocalStorageRemoveItem(key);
                    }
                  }
                }
              }
            }
          } catch (e) {
            console.error("[JIT-UX WindowIdManager] Error scanning for orphaned heartbeats:", e);
          }

          // Update registry with all active IDs (including rescued orphans)
          if (activeWindowIds.length !== registry.length) {
            this.safeLocalStorageSetItem(this._windowIdRegistryKey, JSON.stringify(activeWindowIds));
          }
        } catch (e) {
          console.error("[JIT-UX WindowIdManager] Error during stale window cleanup:", e);
        }
      }, 30000);
    } catch (e) {
      console.error("[JIT-UX WindowIdManager] Error setting up cleanup intervals:", e);
    }
  }
}

// Export a singleton instance
export const windowIdManager = new WindowIdManager();
